import React, { useMemo } from 'react';
import { useProviderConfig } from '../../provider';
import { getDatePickerLocale } from '../DatePickerLocale';
import { DisplayMode } from '../types';
import { getPeriods } from './period';
import { MONTH_RESOLUTION } from './resolution';
import { CalendarInterval, CalendarInputCommonProps, CalendarItem, CalendarPeriodName } from './types';
import { Stack } from '../../stack';
import classNames from 'classnames';
import { useDatePickerAttributes } from '../hooks/useDatePickerAttributes';
import { css } from '@emotion/react';
import { buildCSSWithLength } from '../../../styles';
import { YearMonthCaption } from '../YearMonthCaption';
import { year_month_select_panel_css } from '../styles';
import {
  applyItemInterval,
  findItemsOverlappingWithInterval,
  getIntervalOrNull,
  getNewCursorItem,
  getNextFocus,
  isCursorItemComplete,
  isInstantWithinItem,
  isItemValid,
  isSelectionFromAvailable,
  isSelectionToAvailable,
  isSubintervalEndOfInterval,
  isSubintervalMiddleOfInterval,
  isSubintervalStartOfInterval,
  resolveIntervalWithOffsets,
} from './utils';
import { subMilliseconds } from 'date-fns';

export interface CalendarPeriodInputProps extends CalendarInputCommonProps {
  className?: string;
  period?: CalendarPeriodName;
  shouldConfirm?: boolean;
  canSelect?: boolean;
  onPushPeriod: (period: CalendarPeriodName, viewMonth: Date | null) => void;
  onPopPeriod: (viewMonth: Date | null) => void;
  displayMode: DisplayMode;
  viewMonth?: Date | null;
  onViewMonthChange: (viewMonth: Date | null) => void;
  onItemClick?: (item: CalendarItem) => boolean | void;
}

export const CalendarPeriodInput: React.FC<CalendarPeriodInputProps> = ({
  className,
  constraint,
  resolution = MONTH_RESOLUTION,
  period = 'month',
  items,
  cursor,
  target,
  shouldConfirm = true,
  onCursorChange,
  onConfirm,
  canSelect,
  onPushPeriod,
  onPopPeriod,
  displayMode,
  disabled,
  viewMonth,
  onViewMonthChange,
  onItemClick,
}) => {
  const { panelSize } = useDatePickerAttributes(displayMode);
  const { locale } = useProviderConfig();
  const datePickerLocale = getDatePickerLocale(locale?.lang);
  const {
    name,
    top,
    prev,
    next,
    resolution: periodResolution,
    keepOffset,
    periods,
  } = useMemo(
    () => getPeriods(period, viewMonth ?? new Date(), datePickerLocale),
    [period, viewMonth, datePickerLocale],
  );

  const currentTarget = target ?? cursor?.focus ?? 'from';

  const isDisabled = (date: Date): boolean => {
    if (disabled && canSelect) {
      return true;
    }
    const interval = periodResolution.resolve(date);

    if (currentTarget === 'to') {
      if (cursor?.item?.from != null) {
        const from = cursor.item.from;
        if ((cursor.toggled || target === 'to') && interval.to <= from) {
          return true;
        }
      }
      return !isSelectionToAvailable(constraint, { items }, cursor?.item ?? {}, interval, !cursor?.toggled);
    }

    if (currentTarget === 'from' && cursor?.item?.to != null) {
      const to = cursor.item.to;
      if ((cursor.toggled || target === 'from') && interval.from >= to) {
        return true;
      }
    }

    return !isSelectionFromAvailable(constraint, { items }, cursor?.item ?? {}, interval, !cursor?.toggled);
  };

  const modifiers = useMemo(() => {
    const cursorInterval = getIntervalOrNull(cursor?.item);
    const withPeriodInterval = (fn: (pi: CalendarInterval) => boolean) => (date: Date) => {
      const pi = periodResolution.resolve(date);
      return fn(pi);
    };
    const withCheckItems = (check: (pi: CalendarInterval, interval: CalendarInterval) => boolean) => (date: Date) => {
      const pi = periodResolution.resolve(date);
      if (cursorInterval && check(pi, cursorInterval)) {
        return true;
      }
      return findItemsOverlappingWithInterval(items, pi).some(
        (item) => item.id !== cursor?.item?.id && check(pi, item),
      );
    };
    return {
      selecting: withPeriodInterval((di) => {
        if (cursor?.item?.from && isInstantWithinItem(di, cursor.item.from)) return true;
        if (cursor?.item?.to && isInstantWithinItem(di, subMilliseconds(cursor.item.to, 1))) return true;
        return false;
      }),
      start: withCheckItems((di, interval) => isSubintervalStartOfInterval(di, interval)),
      end: withCheckItems((di, interval) => isSubintervalEndOfInterval(di, interval)),
      middle: withCheckItems((di, interval) => isSubintervalMiddleOfInterval(di, interval)),
    } as const;
  }, [periodResolution, items, cursor]);

  const handleDayMouseEnter = (date: Date) => {
    if (!canSelect) return;
    const interval = resolution.resolve(date);
    const item = applyItemInterval(
      cursor?.item ?? getNewCursorItem(),
      constraint,
      { items },
      interval,
      currentTarget,
    ).item;
    if (currentTarget === 'all') {
      onCursorChange({ ...cursor, fromHover: item?.from, toHover: item?.to });
      return;
    }
    if (!cursor?.active) {
      return;
    }
    if (currentTarget === 'from' || cursor.focus == null) {
      onCursorChange({ ...cursor, fromHover: item?.from });
      return;
    }
    if (currentTarget === 'to' && cursor.item?.from != null) {
      onCursorChange({ ...cursor, toHover: item?.to });
      return;
    }
  };

  const handleDayMouseLeave = () => {
    if (!canSelect) return;
    if (cursor?.fromHover != null || cursor?.toHover != null) {
      onCursorChange({ ...cursor, fromHover: undefined, toHover: undefined });
      return;
    }
  };

  const handleDayClick = (date: Date) => {
    if (canSelect) {
      const interval = resolution.resolve(date);
      if (cursor?.item == null) {
        const selectedItems = findItemsOverlappingWithInterval(items, interval).filter(
          (v) => v.id !== cursor?.item?.id,
        );
        if (selectedItems.length > 0) {
          const result = onItemClick?.(selectedItems[0]);
          if (result === false) {
            return;
          }
        }
      }
      const item = applyItemInterval(
        cursor?.item ?? getNewCursorItem(),
        constraint,
        { items },
        interval,
        currentTarget,
      ).item;

      if (shouldConfirm) {
        if (isCursorItemComplete(item) && isItemValid(constraint, { items }, item)) {
          onConfirm(item);
        } else {
          onCursorChange({ ...cursor, item, focus: getNextFocus(cursor, target), toggled: true });
        }
      } else {
        onCursorChange({ ...cursor, item, focus: cursor?.focus, toggled: true });
      }
    } else {
      if (keepOffset) {
        onPopPeriod(resolveIntervalWithOffsets(periodResolution, date, viewMonth ?? new Date()).from);
      } else {
        onPopPeriod(date);
      }
    }
  };

  return (
    <Stack
      direction='column'
      gap={16}
      className={classNames('pds-month-picker', className)}
      css={[
        css`
          position: relative;
          ${buildCSSWithLength('width', panelSize.width * 3)}
        `,
      ]}
    >
      <YearMonthCaption
        displayMode={displayMode}
        label={name}
        onPrev={() => {
          onViewMonthChange(prev);
        }}
        onNext={() => {
          onViewMonthChange(next);
        }}
        disabledLabel={top == null}
        onClickLabel={() => {
          if (top == null) {
            return;
          }
          onPushPeriod(top, viewMonth ?? null);
        }}
      />
      <Stack wrap='wrap'>
        {periods.map((period, index) => {
          const date = period.interval.from;
          const selecting = modifiers.selecting(date);
          const start = modifiers.start(date) || selecting;
          const end = modifiers.end(date) || selecting;
          const middle = modifiers.middle(date);
          const single = start && end;
          const disabled = isDisabled(date);
          return (
            <button
              type='button'
              className={classNames('pds-year-month-panel', { start, end, single, middle, disabled })}
              disabled={disabled}
              key={index}
              css={[
                year_month_select_panel_css,
                css`
                  ${buildCSSWithLength('width', panelSize.width)};
                  ${buildCSSWithLength('height', panelSize.height)};
                `,
                (start || end) &&
                  css`
                    ${buildCSSWithLength('width', panelSize.width - 2)};
                  `,
              ]}
              onClick={() => handleDayClick(date)}
              onMouseEnter={() => handleDayMouseEnter(date)}
              onMouseLeave={() => handleDayMouseLeave()}
            >
              {period.name}
            </button>
          );
        })}
      </Stack>
    </Stack>
  );
};
